import { Link } from "react-router-dom";
import { CONTAINER, PANEL_BG, SERIF } from "../tokens";

// Page « Demande envoyée » du moteur "agence_v1" — composée avec le
// vocabulaire du bandeau principal (HeroSection.jsx) : pastille d'accroche à
// point vert, titre grotesque dont un mot passe en serif italique, boutons
// blush / contour, et panneau sombre de suivi à droite. La garde d'accès et
// les données viennent de pages/ContactConfirmation.jsx.
export default function ContactConfirmationPage({ copy, name, message, email, phone, homeTo }) {
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, "")}` : "";

  return (
    <section
      data-testid="contact-confirmation"
      className={`${CONTAINER} pt-[84px] pb-[92px] grid gap-[60px] items-center md:grid-cols-2 animate-fade-up`}
    >
      <div>
        <div className="inline-flex items-center gap-[9px] border-control border-ink/[0.16] rounded-full py-[7px] px-[14px] font-display text-[12.5px] tracking-[0.06em] uppercase text-ink/70">
          <span className="h-1.5 w-1.5 rounded-full bg-blush shrink-0" />
          {copy.eyebrow}
        </div>

        <h1 className="font-display font-semibold text-[clamp(34px,4.6vw,58px)] leading-[1.04] tracking-[-0.035em] mt-[26px] text-ink [text-wrap:pretty]">
          <span className="italic font-normal text-gold" style={{ fontFamily: SERIF }}>
            {name ? copy.titleNamed(name) : copy.title}
          </span>
          {copy.titleAfter}
        </h1>

        <p
          data-testid="contact-confirmation-message"
          className="text-[18px] leading-[1.6] text-ink/[0.66] max-w-[520px] mt-6 whitespace-pre-line [text-wrap:pretty]"
        >
          {message}
        </p>

        <div className="flex flex-wrap gap-3 mt-8">
          <Link
            to={homeTo}
            data-testid="contact-confirmation-home"
            className="font-display font-medium text-[15px] bg-blush text-ink py-[14px] px-6 rounded-btn hover:opacity-90 transition-opacity"
          >
            {copy.home}
          </Link>
        </div>
      </div>

      <div
        className="text-cream border-control border-ink/[0.14] rounded-[18px] p-[26px] shadow-[0_26px_60px_-32px_rgba(0,0,0,0.6)]"
        style={PANEL_BG}
      >
        <div className="flex items-center justify-between gap-3.5 flex-wrap">
          <div className="font-display text-[12px] tracking-[0.1em] uppercase text-cream/[0.55]">
            {copy.stepsTitle}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-cream/[0.72]">
            <span className="h-[7px] w-[7px] rounded-full bg-blush" />
            {copy.eyebrow}
          </div>
        </div>

        {/* Première étape faite sur trois : la barre dit « c'est parti »
            sans promettre de délai que la boutique n'aurait pas choisi. */}
        <div className="h-1.5 rounded-full bg-cream/[0.14] mt-6 overflow-hidden">
          <div className="h-full w-1/3 bg-blush" />
        </div>

        <ol className="flex flex-col gap-[18px] mt-[26px] pt-[22px] border-t border-cream/[0.14]">
          {copy.steps.map((step, i) => {
            const done = i === 0;
            return (
              <li key={step.title} className={`flex gap-[11px] ${done ? "" : "text-cream/[0.55]"}`}>
                {/* Glyphes textuels, comme le panneau du bandeau principal. */}
                <span className={`text-[14.5px] leading-[1.45] ${done ? "text-blush" : ""}`} aria-hidden="true">
                  {done ? "✓" : "○"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-[15.5px] font-medium leading-[1.45]">{step.title}</div>
                  <div className={`text-[13.5px] mt-0.5 ${done ? "text-cream/[0.6]" : ""}`}>{step.text}</div>
                </div>
                {done && <span className="text-[13px] text-cream/[0.5] leading-[1.45]">{copy.stepDone}</span>}
              </li>
            );
          })}
        </ol>

        {(email || phone) && (
          <div className="mt-[26px] pt-[22px] border-t border-cream/[0.14]">
            <div className="font-display text-[12px] tracking-[0.1em] uppercase text-cream/[0.55]">
              {copy.urgent}
            </div>
            <div className="flex flex-wrap gap-2 mt-3.5">
              {email && (
                <a
                  href={`mailto:${email}`}
                  className="border-control border-cream/[0.18] rounded-full py-[7px] px-[13px] text-[13px] text-cream/[0.78] hover:text-cream hover:border-cream/40 transition-colors break-all"
                >
                  {email}
                </a>
              )}
              {phone && (
                <a
                  href={telHref}
                  dir="ltr"
                  className="border-control border-cream/[0.18] rounded-full py-[7px] px-[13px] text-[13px] text-cream/[0.78] hover:text-cream hover:border-cream/40 transition-colors"
                >
                  {phone}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
